import { useState, useCallback } from "react";

const GENERAL_FEEDBACK_FIRST_TRIGGER = 3;
const GENERAL_FEEDBACK_RECURRING_INTERVAL = 15;
const STORAGE_KEY_LOGIN_COUNT = "autumn_login_count";
const STORAGE_KEY_LOGIN_FEEDBACK_DISMISSED = "autumn_login_feedback_dismissed";
export const SESSION_KEY_LOGIN_INCREMENTED = 'autumn_login_incremented_this_session';

function getLoginCount(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LOGIN_COUNT);
    if (raw === null) return 0;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
}

function getDismissedThreshold(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LOGIN_FEEDBACK_DISMISSED);
    if (raw === null) return null;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

function getCurrentThreshold(count: number): number | null {
  if (count === GENERAL_FEEDBACK_FIRST_TRIGGER) return GENERAL_FEEDBACK_FIRST_TRIGGER;
  if (
    count > GENERAL_FEEDBACK_FIRST_TRIGGER &&
    (count - GENERAL_FEEDBACK_FIRST_TRIGGER) % GENERAL_FEEDBACK_RECURRING_INTERVAL === 0
  ) {
    return count;
  }
  return null;
}

function computeShouldShow(count: number): boolean {
  const threshold = getCurrentThreshold(count);
  if (threshold === null) return false;
  const dismissed = getDismissedThreshold();
  return dismissed !== threshold;
}

export function useLoginCounter() {
  const initialCount = getLoginCount();
  const [count, setCount] = useState(initialCount);
  const [shouldShowFeedback, setShouldShowFeedback] = useState(() =>
    computeShouldShow(initialCount)
  );

  const incrementLoginCount = useCallback(() => {
    const current = getLoginCount();
    const next = current + 1;
    try {
      localStorage.setItem(STORAGE_KEY_LOGIN_COUNT, String(next));
    } catch {
      return;
    }
    setCount(next);
    setShouldShowFeedback(computeShouldShow(next));
  }, []);

  const dismissFeedback = useCallback(() => {
    const threshold = getCurrentThreshold(count);
    if (threshold !== null) {
      try {
        localStorage.setItem(STORAGE_KEY_LOGIN_FEEDBACK_DISMISSED, String(threshold));
      } catch {
        // localStorage full — still update UI state
      }
    }
    setShouldShowFeedback(false);
  }, [count]);

  return { shouldShowFeedback, dismissFeedback, incrementLoginCount };
}
